<script setup lang="ts">
  import { useMenuStore } from '@/composition/use-menu';
  import { useMenuV2 } from '@/composition/use-menu-v2';
  import { menuPagePath } from '@/domain/catalog/menu-links';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { computed } from 'vue';
  import { RouterLink } from 'vue-router';

  const restaurantStore = useRestaurantStore();
  const { isMenuV2Active } = useMenuV2();
  const menuStore = useMenuStore();

  // This component shows suggestions to improve the restaurant menu, in form of a list that show a progress when incomplete, or a checkmark when complete
  /*
    The suggestions are:
    - Add photos to all items (progress meter)
    - Add descriptions to all items (progress meter)
    - Enable AI translations (checkmark)
  */

  const suggestions = computed(() => {
    if (
      !restaurantStore.restaurant ||
      !restaurantStore.selectedBranch ||
      !menuStore.visibleItems
    )
      return null;

    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    const totalItems = menuStore.visibleItems.length;
    const hasPaidAccess = restaurantStore.effectiveTier > 0;

    const progress = {
      itemsWithPhotos: 0,
      itemsWithDescriptions: 0,
    };

    for (const item of menuStore.visibleItems) {
      if (item.picture) progress.itemsWithPhotos++;
      if (item.description) progress.itemsWithDescriptions++;
    }

    const iaTranslationsEnabled = hasPaidAccess && !!restaurant?.languageIds?.length;

    const orderAllowed = hasPaidAccess && branch?.allowOrders;

    const hasSocialNetworks = hasPaidAccess && !!branch?.links?.length;

    return [
      {
        title: 'Agregue fotos a todos los productos',
        progress: (progress.itemsWithPhotos / totalItems) * 100,
        complete: totalItems > 0 && progress.itemsWithPhotos === totalItems,
        tooltip: `Fotos añadidas a ${progress.itemsWithPhotos} de ${totalItems} productos`,
        icon: 'mdi-camera',
        link: menuPagePath(
          restaurantStore.restaurant?.stringId ?? '',
          isMenuV2Active.value,
          { filter: 'no-photo' }
        ),
      },
      {
        title: 'Agregue descripción a todos los productos',
        progress: (progress.itemsWithDescriptions / totalItems) * 100,
        complete:
          totalItems > 0 && progress.itemsWithDescriptions === totalItems,
        tooltip: `Descripciones añadidas a ${progress.itemsWithDescriptions} de ${totalItems} productos`,
        icon: 'mdi-format-text',
        link: menuPagePath(
          restaurantStore.restaurant?.stringId ?? '',
          isMenuV2Active.value,
          { filter: 'no-description' }
        ),
      },
      {
        title: 'Habilite las traducciones con IA',
        complete: iaTranslationsEnabled,
        icon: 'mdi-web',
        tooltip: iaTranslationsEnabled
          ? `Traducciones habilitadas a ${restaurant?.languageIds?.length} idiomas`
          : 'Haga su menú accesible en varios idiomas',
        link: `/${restaurantStore.restaurant?.stringId}/catalog/settings`,
      },
      {
        title: 'Agregue sus redes sociales',
        complete: hasSocialNetworks,
        icon: 'mdi-share-variant',
        tooltip: hasSocialNetworks
          ? 'Redes sociales añadidas'
          : 'Comparta sus redes sociales con sus clientes',
        link: `/${restaurantStore.restaurant?.stringId}/settings?tab=info`,
      },
      {
        title: 'Habilite los pedidos',
        complete: orderAllowed,
        icon: 'mdi-cart',
        tooltip: orderAllowed
          ? 'Pedidos habilitados'
          : 'Permita que los clientes hagan pedidos desde el menú',
        link: `/${restaurantStore.restaurant?.stringId}/settings?tab=orders`,
      },
    ];
  });
</script>

<template>
  <div class="dashboard-improvement">
    <div class="title-container">
      <v-icon class="icon">mdi-lightbulb-on</v-icon>
      <span class="c-title">Mejore su menú</span>
    </div>
    <div class="suggestions">
      <template v-if="!suggestions">
        <v-skeleton-loader type="list-item-avatar" />
        <v-skeleton-loader type="list-item-avatar" />
      </template>
      <template v-else>
        <div
          v-for="suggestion in suggestions"
          :key="suggestion.title"
          class="suggestion"
        >
          <template v-if="suggestion.complete">
            <div class="complete">
              <v-icon class="icon" small>mdi-check</v-icon>
            </div>
          </template>
          <template v-else>
            <v-progress-circular
              class="icon"
              :value="suggestion.progress ?? 0"
              color="purple"
              :size="23"
            ></v-progress-circular>
          </template>
          <div class="content">
            <RouterLink :to="suggestion.link" class="c-title">{{
              suggestion.title
            }}</RouterLink>
            <RouterLink :to="suggestion.link" class="c-subtitle">{{
              suggestion.tooltip
            }}</RouterLink>
          </div>
          <v-btn
            v-if="!suggestion.complete"
            color="primary"
            small
            icon
            :to="suggestion.link"
          >
            <v-icon>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .dashboard-improvement {
    @apply tw-space-y-2;

    .title-container {
      @apply tw-flex tw-items-center tw-space-x-2;

      .icon {
        @apply tw-text-purple-500;
      }

      .c-title {
        @apply tw-font-semibold tw-text-lg;
      }
    }

    .suggestions {
      @apply tw-space-y-2;

      .suggestion {
        @apply tw-flex tw-items-center tw-space-x-4;

        .complete {
          @apply tw-bg-green-500 tw-rounded-full tw-h-6 tw-w-6 tw-flex tw-items-center tw-justify-center;

          .icon {
            @apply tw-text-white;
          }
        }

        .content {
          @apply tw-flex-1 tw-min-w-1;
          @apply tw-flex tw-flex-col;
        }

        .c-title {
          @apply tw-font-semibold tw-text-neutral-500;
        }

        .c-subtitle {
          @apply tw-text-xs tw-text-gray-500;
        }
      }
    }
  }
</style>
